import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { FeatureRequestSource } from "@shared/schemas/featureRequest";

/**
 * Global "Suggest a feature" dialog state.
 *
 * Every entry point (footer link, Profile card, post-generation CTA, anywhere
 * else we add later) calls `useFeatureRequestDialog().open(source)`. The
 * dialog itself is mounted exactly once at the app root — adding a new
 * trigger is a one-liner, never a copy of the dialog markup.
 *
 * Mirrors the AuthModalContext shape (provider + hook) so the contributor
 * mental model is identical.
 */

interface FeatureRequestDialogContextValue {
  isOpen: boolean;
  /** Where the dialog was opened from. Carried into the submitted row + every PostHog event. */
  source: FeatureRequestSource;
  open: (source: FeatureRequestSource) => void;
  close: () => void;
}

const FeatureRequestDialogContext = createContext<
  FeatureRequestDialogContextValue | undefined
>(undefined);

export function FeatureRequestDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState<FeatureRequestSource>("other");

  const open = useCallback((src: FeatureRequestSource) => {
    setSource(src);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({ isOpen, source, open, close }),
    [isOpen, source, open, close],
  );

  return (
    <FeatureRequestDialogContext.Provider value={value}>
      {children}
    </FeatureRequestDialogContext.Provider>
  );
}

export function useFeatureRequestDialog(): FeatureRequestDialogContextValue {
  const ctx = useContext(FeatureRequestDialogContext);
  if (!ctx) {
    throw new Error(
      "useFeatureRequestDialog must be used within a FeatureRequestDialogProvider",
    );
  }
  return ctx;
}
